import { Bug, ArrowUp, MessageSquareMore, Lock } from 'lucide-react';
import ConfirmedBugsSeverity from './ConfirmedBugsSeverity';

function PipelineBox({ label, value, className }) {
  return (
    <div className={`pipeline-box ${className}`}>
      <span className="pipeline-num">{value}</span>
      {label}
    </div>
  );
}

function RejectBox({ label, value }) {
  return (
    <div className="reject-box">
      <span className="rnum">{value}</span>
      <span className="rlabel">{label}</span>
    </div>
  );
}

function PendingBox({ value }) {
  return (
    <div className="reject-box pending-box">
      <span className="rnum">{value}</span>
      <span className="rlabel">Pending</span>
    </div>
  );
}

export function BugKpiBlock({ data }) {
  const { totalFeedback, bugs, improvements, confirmedBugs, confirmedBugsLocked } = data;

  return (
    <div className="sidebar-kpi-block">
      <div className="sidebar-kpi-section">
        <div className="sidebar-kpi-label">
          <span className="kpi-pill feedback">
            <span className="kpi-pill-header"><MessageSquareMore size={12} /> Total Feedback</span>
            <strong className="kpi-pill-value">{totalFeedback}</strong>
          </span>
        </div>
        <div className="sidebar-kpi-sub">
          <span className="kpi-pill bug">
            <span className="kpi-pill-header"><Bug size={12} /> Bugs</span>
            <strong className="kpi-pill-value">{bugs}</strong>
          </span>
          <span className="kpi-pill improvement">
            <span className="kpi-pill-header"><ArrowUp size={12} /> Improvements</span>
            <strong className="kpi-pill-value">{improvements}</strong>
          </span>
        </div>
      </div>
      <div className="sidebar-kpi-section confirmed">
        <span className="kpi-pill bug">
          <span className="kpi-pill-header">
            <Bug size={12} /> Confirmed Bugs
            {confirmedBugsLocked && (
              <span className="kpi-lock-wrap">
                <button type="button" className="kpi-lock-btn" title="This number is locked">
                  <Lock size={12} />
                </button>
                <span className="locked-bugs-popup" role="tooltip">
                  This value represents the confirmed bugs at end of campaign and will not update
                  when bugs are updated by product units.
                </span>
              </span>
            )}
          </span>
          <strong className="kpi-pill-value">{confirmedBugs}</strong>
        </span>
      </div>
    </div>
  );
}

export function BugPipelinePanel({ data }) {
  const { fixStatus, rejectedByQT, rejectedByPU } = data;
  const closed = fixStatus.closedDone + fixStatus.closedOther;

  return (
    <div className="pipeline-and-rejected">
      <div>
        <div className="pipeline-section-title">Overall Status of Bug fixes</div>
        <div className="fix-pipeline">
          <PipelineBox label="Backlog" value={fixStatus.backlog} className="backlog" />
          <span className="pipeline-arrow">›</span>
          <PipelineBox label="In Progress" value={fixStatus.inProgress} className="in-progress" />
          <span className="pipeline-arrow">›</span>
          <PipelineBox label="In Review" value={fixStatus.inReview} className="in-review" />
          <span className="pipeline-arrow">›</span>
          <PipelineBox label="Under Testing" value={fixStatus.underTesting} className="under-testing" />
          <span className="pipeline-arrow">›</span>
          <PipelineBox label="Pending Deployment" value={fixStatus.pendingDeployment} className="pending-deploy" />
          <span className="pipeline-arrow">›</span>
          <div className="closed-wrap">
            <PipelineBox label="Closed" value={closed} className="closed" />
            <div className="closed-fork-stem" />
            <div className="closed-split">
              <div className="closed-sub-box other">
                <span className="pipeline-num">{fixStatus.closedOther}</span>Other
              </div>
              <div className="closed-sub-box done">
                <span className="pipeline-num">{fixStatus.closedDone}</span>Done
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rejected-stack">
        <div className="rejected-row">
          <span className="rejected-label">Rejected by QT</span>
          <div className="rejected-counts">
            <RejectBox label="Not a Bug" value={rejectedByQT.notABug} />
            <RejectBox label="Duplicate" value={rejectedByQT.duplicate} />
            <RejectBox label="Cannot Reproduce" value={rejectedByQT.cannotReproduce} />
            <RejectBox label="Other" value={rejectedByQT.other} />
          </div>
          <div className="pending-divider" />
          <PendingBox value={rejectedByQT.pending} />
        </div>
        <div className="rejected-row">
          <span className="rejected-label">Rejected by PU</span>
          <div className="rejected-counts">
            <RejectBox label="Not a Bug" value={rejectedByPU.notABug} />
            <RejectBox label="Duplicate" value={rejectedByPU.duplicate} />
            <RejectBox label="Cannot Reproduce" value={rejectedByPU.cannotReproduce} />
            <RejectBox label="Other" value={rejectedByPU.other} />
          </div>
          <div className="pending-divider" />
          <PendingBox value={rejectedByPU.pending} />
        </div>
      </div>
    </div>
  );
}

export function BugStatsPanel({ data }) {
  return (
    <>
      <div className="executive-layout">
        <div className="exec-left-stack">
          <BugKpiBlock data={data} />
        </div>
        <BugPipelinePanel data={data} />
      </div>
    </>
  );
}

function ExecutiveView({ severityData }) {
  return severityData ? (
    <ConfirmedBugsSeverity
      byComponent={severityData.byComponent}
      byUnit={severityData.byUnit}
      noHeader={false}
    />
  ) : null;
}

export default ExecutiveView;
